"use client";

/**
 * Single registration point for GSAP and its plugins.
 *
 * Every motion component imports gsap/ScrollTrigger/SplitText/useGSAP from
 * this module, never from the packages directly, so plugin registration
 * happens exactly once per client bundle. registerPlugin is idempotent, so
 * StrictMode double-invocation and multiple importers are both safe.
 *
 * SSR safety: registration only runs in the browser. The gsap core module
 * itself is SSR-safe to import (no window access at module scope).
 */
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, SplitText, useGSAP);
}

/**
 * True when the user has asked the OS for reduced motion. Checked at
 * animation-setup time inside effects (never at module scope). Components
 * bail out before creating any tween or initial hidden state, so reduced
 * motion users get fully static, fully visible content.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export { gsap, ScrollTrigger, SplitText, useGSAP };
